import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import cls from 'classnames';
import { Button, Dropdown, SegmentMultiple, Segment, Checkbox } from 'tea-component';
import IconDom from '@intelligence/components/SelectCompoenet/ItemDom/IconDom';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import { getPCConsoleMetricValues } from '@intelligence/pages/TopCharts/PCGames/_config/utils';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import InfoDocEN from '@intelligence/_locales/en-US';
import InfoDocCN from '@intelligence/_locales/zh-CN';
import { allMetricsOptions, hideByDateType, getMetricOptionByValue } from '../MetricsSelection/config';
import MetricBox from './MetricBox';
import { filterByDateType } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/AllMetrics/utils';
import { getREGHeightObjText } from '@intelligence/utils/common';
import MetricSearchBox from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricSelect/MetricSearchBox';
import { scheduleAdjustDropdownPosition } from './utils/autoScrollTop';
import { startLimitMetricOverlayTop } from '@intelligence/components/SelectCompoenet/MetricSelect/utils/limitOverlayTop';

import './index.scss';

// 临时方案
const needProcessMetricsValue = [
  'ampere_d1',
  'ampere_d7',
  'ampere_d14',
  'ampere_d28',
  'ampere_d60',
  'ampere_rolling_d1',
  'ampere_rolling_d7',
  'ampere_rolling_d14',
  'ampere_rolling_d28',
  'ampere_rolling_d60',
];

const getLegacyMetricType = (metric: any) => metric?.metricsType;

interface MetricSelectorProp {
  // 弹窗列表，未过滤
  metricTypeOptions: { text: React.ReactNode; value: string }[];
  metricTypeAllOptions: { text: React.ReactNode; value: string }[];
  sourcesOptions: { text: React.ReactNode; value: string }[];
  metricsOptions: { text: React.ReactNode; value: string }[];
  // 上次用户选择的值

  metricTypeVals: string;
  sourceVals: string[];
  oldMetricsVals: string[];
  // reset默认值

  defaultMetricTypeVals: string;
  defaultSourceVals: string[];
  defaultMetricsVals: string[];
  // 交互函数
  hendalConfirm: (params: {
    metricsObj: any;
    metricsOptions: any[];
    metricsValue: string[];
    metricTypeValue: string;
    sourcesValue: string[];
    dateTypeValue?: string;
  }) => void;
  loading?: boolean;
  type: 'daily' | 'weekly' | 'monthly';
  handleReset?: any;
  dateTypeValue?: any;
  setDateTypeValue?: any;
  metricsValueList?: any;
  setMetricsValueList?: any;
  setShotSelect?: any;
  shotName?: string;
  defaultDateType?: string;
  getDataByValue?: any;
  setSelectCutValue?: any;
  overlayClassName?: string;
  // whether DataBrain data is available for current context
  hasDatabrainData?: boolean;
  resolveMetricType?: (metric: any) => string | undefined;
  disableSourcesWithoutMetrics?: boolean;
}
const MetricSelect = (props: MetricSelectorProp) => {
  const {
    metricTypeVals = 'all',
    sourceVals = [],
    oldMetricsVals = [],
    metricsOptions = [], // 当前粒度全部指标
    sourcesOptions = [],
    metricTypeOptions = [],
    metricTypeAllOptions = [],
    defaultSourceVals = [],
    defaultMetricsVals = [],
    hendalConfirm,
    handleReset,
    loading,
    type,
    shotName,
    dateTypeValue,
    setDateTypeValue,
    metricsValueList: metricsValue = [],
    setMetricsValueList: setMetricsValue,
    defaultDateType,
    setShotSelect,
    getDataByValue,
    setSelectCutValue,
    overlayClassName,
    hasDatabrainData = false,
    resolveMetricType = getLegacyMetricType,
    disableSourcesWithoutMetrics = false,
  } = props;

  // console.log('MetricSelect~~~~~~~~~~~~~~~', props);
  const { formatMessage, locale } = useIntlExtend();
  const selectorRef = useRef<HTMLDivElement>(null);
  const stopLimitOverlayTopRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => {
      stopLimitOverlayTopRef.current?.();
      stopLimitOverlayTopRef.current = null;
    };
  }, []);

  const [inputValue, setInputValue] = useState('');
  const [metricTypeValue, setMetricTypeValue] = useState<string>(metricTypeVals);
  // No legacy normalization needed; use values as-is (databrain)
  const normalizeSource = (v: string) => v;
  const normalizeSourcesArray = (arr: string[] = []) => arr?.map(normalizeSource);
  const [sourcesValue, setSourcesValueRaw] = useState<string[]>(normalizeSourcesArray(sourceVals));
  const [searchData, setSearchData] = useState<any[]>([]);
  // 取 metricType, dataSource 交集指标，当前 Metrics Tags 展示
  const [metricOptionsFromBoth, setMetricOptionsFromBoth] = useState<any[]>([]);

  const selectMetricsList = useMemo(() => {
    const normalizedValues = Array.isArray(metricsValue) ? metricsValue : [];
    if (!normalizedValues.length) {
      return [];
    }
    if (normalizedValues.includes('all')) {
      return metricOptionsFromBoth;
    }
    return getMetricOptionByValue(normalizedValues, false);
  }, [metricsValue, metricOptionsFromBoth]);

  // wrapper setter to always normalize incoming values
  const setSourcesValue: any = (vals: string[] | ((prev: string[]) => string[])) => {
    if (typeof vals === 'function') {
      // normalize result of updater
      setSourcesValueRaw((prev) => normalizeSourcesArray((vals as any)(prev)));
    } else {
      setSourcesValueRaw(normalizeSourcesArray(vals));
    }
  };

  // function processOptsLabel({ opts, middle, targetList, splitor }) {
  //   const needReplace = middle && splitor;

  //   return opts?.map((item: any) => {
  //     if (targetList?.includes(item?.value)) {
  //       const textStrArr = item?.text?.split(splitor);
  //       const rawMetricText = textStrArr?.[textStrArr?.length - 1];

  //       return {
  //         ...item,
  //         text: needReplace
  //           ? formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
  //           : formatMessage({ id: item?.text || item?.value }),
  //       };
  //     }

  //     return item;
  //   });
  // }

  const isMonthly = dateTypeValue === 'monthly';
  const middle = isMonthly ? 'monthly_' : '';
  const splitor = 'dbi_metric_';

  const closeDropdown = () => {
    // 模拟窗口点击，关闭下拉弹窗
    const evn = document.createEvent('MouseEvents');
    evn.initEvent('mousedown', true, true);
    document.body.dispatchEvent(evn);
  };

  const renderMetricsOptions = (params: {
    opts: any[];
    showTotal: boolean;
    hasAll: boolean;
    showIcon: boolean;
    showCheckbox?: boolean;
    inputValue?: string;
  }) => {
    const { opts, showTotal, hasAll, showIcon, showCheckbox } = params;

    const optionData = opts?.map((e) => {
      const isChecked = metricsValue?.includes(e.value);
      const textStrArr = e?.text?.split(splitor);
      const rawMetricText = textStrArr?.[textStrArr?.length - 1];

      const iconDom = (
        <IconDom
          data={{
            text: inputValue ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: getREGHeightObjText(
                    // formatMessage({ id: e?.text || e?.value }),
                    isMonthly && needProcessMetricsValue.includes(e?.value)
                      ? formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
                      : formatMessage({ id: e?.text || e?.value }),
                    inputValue,
                  ),
                }}
              ></span>
            ) : // formatMessage({ id: `${middle}${e?.text}` || `${middle}${e?.value}` })
            isMonthly && needProcessMetricsValue.includes(e?.value) ? (
              formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
            ) : (
              formatMessage({ id: e?.text || e?.value })
            ),
            img: getDataSourceIcon(e?.icon ? e?.icon : e?.dataSource || e?.value),
          }}
        />
      );

      return {
        ...e,
        value: e?.value,
        // textStr_tmp: formatMessage({ id: e?.text || e?.value }),
        textStr_tmp:
          isMonthly && needProcessMetricsValue.includes(e?.value)
            ? formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
            : formatMessage({ id: e?.text || e?.value }),
        key: e?.text || e?.value,
        text: (() => {
          // 展示复选框+图标文案（输入框搜索时展示选项列表）
          if (showCheckbox) {
            return (
              <div className="dbi-search-auto-complete-item">
                <Checkbox value={isChecked} />
                {iconDom}
              </div>
            );
          }
          // 展示图标文案
          if (showIcon) {
            return iconDom;
          }
          // 仅展示文案
          // return formatMessage({ id: e?.text || e?.value });
          return isMonthly && needProcessMetricsValue.includes(e?.value)
            ? formatMessage({ id: `${splitor}${middle}${rawMetricText}` })
            : formatMessage({ id: e?.text || e?.value });
        })(),
      };
    });

    if (hasAll) {
      optionData.unshift({
        text: (
          <>
            {showTotal
              ? `${formatMessage({ id: 'dbi_all' })}(${opts?.length})`
              : formatMessage({ id: 'dbi_all' })}
          </>
        ),
        textStr_tmp: 'All',
        value: 'all',
      });
    }

    return optionData;
  };

  const getIconCur = (item: any, sourcesValue: any) => {
    const ifHasNewzoo = sourcesValue?.includes?.('newzoo');
    const ifHasAmpere = sourcesValue?.includes?.('ampere');
    const ifHasDatabrainSelected = sourcesValue?.includes?.('databrain');
    const ifHasAlineaSelected = sourcesValue?.includes?.('alinea');
    if (ifHasNewzoo && item?.value === 'newzoo') {
      return 'newzoowhite';
    }
    if (ifHasAmpere && item?.value === 'ampere') {
      return 'amperewhite';
    }
    if (ifHasDatabrainSelected && item?.value === 'databrainbluebg') {
      return 'databrainbgresized';
    }
    if (ifHasAlineaSelected && item?.value === 'alinea') {
      return 'alineawhite';
    }
    return item?.value;
  };

  const renderMetricsOptionsDom = useMemo(() => {
    const ifHasDataBrain = !!hasDatabrainData;
    const typeEnum = hideByDateType[type];
    const optionData = sourcesOptions?.map((e: any) => {
      const hasMetricForSource = metricsOptions.some((metric) => metric?.dataSource === e.value);
      const obj: any = {
        all: '',
        mscience: '',
        npd: formatMessage({ id: 'dbi_info_physical_monthly_only' }),
        gsd: formatMessage({ id: 'dbi_info_gsd_metrics_category_disabled_tips' }),
        vginsights: formatMessage({ id: 'dbi_info_vgi_metrics_category_disabled_tips' }),
        // { text: 'Other Vendor', value: 'ampere' },
        ampere: formatMessage({ id: 'dbi_info_other_vendor_disabled_tips' }),
        databrain: formatMessage({ id: 'dbi_info_databrain_game_nodata_tips' }),
      };
      // 合并既有规则与新增规则，避免当前粒度判断覆盖 Source 自身的 disabled 状态。
      const isDisabled = Boolean(
        e?.disabled ||
          e?.disabledType ||
          typeEnum?.includes(e?.value) ||
          (disableSourcesWithoutMetrics && !hasMetricForSource) ||
          (!hasDatabrainData && e?.value === 'databrain'),
      );
      return {
        ...e,
        value: e?.value,
        textStr_tmp: formatMessage({ id: e?.text }),
        key: e?.text,
        disabled: isDisabled,
        tooltip:
          isDisabled ? obj?.[e?.value] : '',
        // bubble: e?.disabledType ? "No data" : ''
        text: (
          <IconDom
            data={{
              text: formatMessage({ id: e?.text || e?.value }),
              // base icon from config (e.icon) or value; then apply selection highlight via getIconCur
              img: getDataSourceIcon(
                getIconCur({ value: e?.icon ? e?.icon : e?.value }, sourcesValue),
              ),
              disabled: isDisabled,
            }}
          />
        ),
      };
    });
    optionData?.unshift({
      text: <>{`${formatMessage({ id: 'dbi_all' })}`}</>,
      textStr_tmp: 'All',
      value: 'all',
    });
    return optionData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesOptions, metricsOptions, type, hasDatabrainData, sourcesValue, disableSourcesWithoutMetrics, formatMessage]);

  useEffect(() => {
    // 当前所有展示的 Sources 配置项的值（kpiTrends 页不展示 options 选项，所以不应出现在搜索列表中）
    let currentAllSourcesOptionsValue = sourcesOptions?.map((t: any) => t.value);
    if (!hasDatabrainData) {
      currentAllSourcesOptionsValue = currentAllSourcesOptionsValue?.filter(
        (v: string) => v !== 'databrain',
      );
    }

    // 从全量 metric options 配置中筛选出当前组件展示的数据源数据
    const currentAllOpts = allMetricsOptions.filter((item) =>
      currentAllSourcesOptionsValue?.includes(item?.dataSource),
    );

    const searchData = renderMetricsOptions({
      opts: currentAllOpts,
      hasAll: false,
      showTotal: false,
      showIcon: true,
      showCheckbox: true,
      inputValue,
    });

    // metric type 对应值
    const metricTypeValueTemp =
      metricTypeValue === 'all' ? metricTypeAllOptions?.map((t) => t.value) : [metricTypeValue];
    // 数据源类型对应值
    const sourcesValueTemp = sourcesValue?.includes('all')
      ? currentAllSourcesOptionsValue
      : sourcesValue;

    // 从所有当前粒度可用选项中筛选，取出当前选中的 metricType 以及当前选中数据源类型对应的 options 的并集
    const metricValueFromBoth = metricsOptions?.filter((t: any) => {
      const passSource = sourcesValueTemp?.includes(t?.dataSource);
      // All 表示不施加 Metric Type 限制；Compare 的 Opinions 仅在 Source 中出现，不能因未出现在 Type 菜单而被过滤掉。
      const passType = metricTypeValue?.includes('all') || metricTypeValueTemp?.includes(resolveMetricType(t));
      const passDatabrain = hasDatabrainData || t?.dataSource !== 'databrain';
      return passSource && passType && passDatabrain;
    });

    // 更新数据状态
    setMetricOptionsFromBoth(metricValueFromBoth);
    setSearchData(searchData);
  }, [
    JSON.stringify(metricTypeValue),
    JSON.stringify(sourcesValue),
    JSON.stringify(metricsOptions),
    // renderMetricsOptions 函数依赖判断复选框是否选中
    metricsValue,
    inputValue,
    locale,
    hasDatabrainData,
    resolveMetricType,
  ]);

  // Clean up preselected databrain when it is not available
  useEffect(() => {
    if (!hasDatabrainData && sourcesValue?.includes('databrain')) {
      setSourcesValue(sourcesValue?.filter((v: string) => v !== 'databrain'));
    }
  }, [hasDatabrainData]);

  // 接收2个参数，返回一个函数
  const createOnChange = (currentValue: string[], setValue: Function) => (value: string[]) => {
    // console.log('MetricSelect~~~~~currentValue~~~~~~~~~~', currentValue);
    // console.log('MetricSelect~~~~~~~~~~~~~~value~', value);
    const hasAll = currentValue?.includes('all');
    // console.log('MetricSelect~~~~~~~~~~~~~~hasAll~', hasAll);
    if (value.length === 0) return;
    // 选择了all，但是非全选，则给它全选
    if (value[0] === 'all') {
      // 如果有All, 那么根据之前是否有all, 判断是删除all, 还是只保留all
      hasAll ? value.shift() : (value = ['all']);
    }
    // console.log('MetricSelect~~~~~~~value222~~~~~~~~', value);
    setValue?.(value);
  };
  const onChangeSub =
    (currentValue: string[] | string, setValue: Function) => (value: string[]) => {
      setValue?.(value);
    };

  const metricRender = useMemo(() => {
    if (loading) {
      return [];
    }
    return renderMetricsOptions({
      opts: metricsValue?.includes('all') ? metricOptionsFromBoth : selectMetricsList,
      hasAll: false,
      showTotal: false,
      showIcon: true,
    });
  }, [loading, metricsValue, metricOptionsFromBoth, selectMetricsList]);

  // 输入框搜索时的选项列表
  const searchOptions = useMemo(() => {
    if (!inputValue) return;

    return searchData?.filter((t) => {
      const name =
        (InfoDocEN as any)?.[t?.key] + (InfoDocCN as any)?.[t?.key] + t?.textStr_tmp;
      return name?.toLocaleLowerCase()?.includes(inputValue?.toLocaleLowerCase());
    });
  }, [searchData, inputValue]);

  useDeepCompareEffect(() => {
    setMetricTypeValue(metricTypeVals || 'all');
    const result: any = [];
    const normalizedSourceVals = normalizeSourcesArray(sourceVals);
    renderMetricsOptionsDom?.forEach((item: any) => {
      // 如果是添加
      if (normalizedSourceVals?.includes(item.value)) {
        result.push(item.value);
      }
    });
    setSourcesValue(result || ['all']);
  }, [metricTypeVals, sourceVals]);

  const handleDropdownClose = () => {
    stopLimitOverlayTopRef.current?.();
    stopLimitOverlayTopRef.current = null;

    const isSame =
      metricsValue?.length === oldMetricsVals?.length &&
      metricsValue?.every((element: any) => oldMetricsVals?.includes(element));
    if (!isSame) {
      setMetricsValue(oldMetricsVals);
      setDateTypeValue(defaultDateType);
    }

    // 清空内部搜索输入框
    setInputValue('');
  };

  // 深度监听type -- 迁移至筛选器组件层
  // useDeepCompareEffect(() => {
  //   const { filterList } = filterByDateType(metricsValueList, typeCur);
  //   // 如果有数据，则更新，否则使用默认值
  //   if (defaultMetrics?.length) {
  //     if (filterList?.length) {
  //       setMetricsValueList(filterList);
  //     } else {
  //       // 默认不要保存,否则每次进来都是相同数据
  //       setMetricsValueList(defaultListCur);
  //       setShotSelect('all');
  //     }
  //   }
  // }, [typeCur]);

  // 切换时间粒度
  const handleDateType = (value: any) => {
    const options = getDataByValue(metricsValue);
    const { filterList } = filterByDateType(options, value);
    // 如果有数据，则更新，否则使用默认值
    if (filterList?.length) {
      setMetricsValue(filterList);
    } else {
      // 默认不要保存,否则每次进来都是相同数据
      setMetricsValue(defaultMetricsVals);
    }
    setSourcesValue(defaultSourceVals);
    setShotSelect('all');
    setSelectCutValue('all');
    setDateTypeValue(value);
  };

  return (
    <>
      <Dropdown
        placement="top-end"
        escapeWithReference={true}
        onOpen={() => {
          scheduleAdjustDropdownPosition(selectorRef.current);
          stopLimitOverlayTopRef.current?.();
          stopLimitOverlayTopRef.current = startLimitMetricOverlayTop(
            '.dbi-metric-select-auto-scroll-overlay .tea-dropdown-box, .dbi-kpi-trends-metric-filter-overlay .tea-dropdown-box',
          );
        }}
        onClose={handleDropdownClose}
        matchButtonWidth
        closeOnScroll={false}
        className={`dbi-metric-select-box ${locale == 'en-US' ? 'en' : 'ch'}`}
        overlayClassName={cls('dbi-metric-select-overlay', overlayClassName)}
        button={
          <div className="dbi-metric-select" ref={selectorRef}>
            <div className="dbi-metric-select-lable">
              {shotName
                ? `${formatMessage({ id: shotName })}(${metricRender?.length})`
                : formatMessage({ id: 'dbi_top_metric_intotal' }, { number: metricRender?.length })}
            </div>
            {!metricsValue?.length && (
              <div className="dbi-metric-select-lable">
                {formatMessage({ id: 'dbi_top_placeholder_metrics' })}
              </div>
            )}
            <div className="dbi-metric-select-text">
              <div className="dbi-metric-select-value">
                {metricRender?.map((e: any, index: number) => {
                  return (
                    <>
                      <span className="dbi-metric-select-span" key={e.text + e?.value}>
                        {e.text}
                        {index !== metricRender?.length - 1 && <>,&nbsp;</>}
                      </span>
                    </>
                  );
                })}
              </div>
            </div>
          </div>
        }
        appearance="button"
        clickClose={false}
        footer={
          <div className="dbi-metric-footer">
            <Button
              onClick={() => {
                // 对数据进得处理
                const parseValue = metricRender?.map((t) => {
                  return {
                    ...t,
                    value: t?.value,
                    text: t?.key ?? t?.textStr_tmp, // t?.textStr_tmp ?? t?.text,
                    textStr_tmp: undefined,
                  };
                });
                const metricsObj = getPCConsoleMetricValues(parseValue);
                hendalConfirm?.({
                  metricsObj,
                  metricsOptions: parseValue,
                  metricsValue,
                  metricTypeValue,
                  sourcesValue,
                });
                setTimeout(() => {
                  // 优先更新confirm，然后再调用到closeDropdown，保证父组件的metricsVals已经更新
                  closeDropdown();
                }, 10);
              }}
              type="primary"
              disabled={
                metricsValue?.length === 0 ||
                (metricOptionsFromBoth?.length === 0 && metricsValue?.includes('all'))
              }
            >
              {formatMessage({ id: 'dbi_confirm' })}
            </Button>
            <Button
              onClick={() => {
                // closeDropdown();
                // Reset展示为产品给的默认指标
                setMetricsValue(defaultMetricsVals);
                setMetricTypeValue('all');
                setSourcesValue(defaultSourceVals);
                handleReset();
              }}
            >
              {formatMessage({ id: 'dbi_reset' })}
            </Button>
          </div>
        }
      >
        {(_close) => (
          <div className="dbi-metric-box">
            {/* Metrics 搜索框 */}
            <MetricSearchBox
              inputValue={inputValue}
              setInputValue={setInputValue}
              onChange={setMetricsValue}
              setSourcesValue={setSourcesValue}
              dateGranularityType={dateTypeValue || type}
              searchOptions={searchOptions}
              selectMetricsList={selectMetricsList}
            />

            <div className="dbi-metric-form dbi_small_height">
              {/* 时间粒度 Segment 选项 */}
              <div className="dbi-metric-row dbi-metric-row-new">
                <div className="dbi-metric-lable">
                  {formatMessage({ id: 'dbi_top_granularity' })}
                </div>
                <div className="dbi-metric-context">
                  <Segment
                    value={dateTypeValue}
                    onChange={handleDateType}
                    options={[
                      { text: formatMessage({ id: 'dbi_day' }), value: 'daily' },
                      { text: formatMessage({ id: 'dbi_week' }), value: 'weekly' },
                      { text: formatMessage({ id: 'dbi_month' }), value: 'monthly' },
                    ]}
                  />
                  <span className="dbi-metric-timely-lable">{formatMessage({ id: 'dbi_top_granularity_tips' })}</span>
                </div>
              </div>

              {/* MetricType 指标类型 Segment 选项 */}
              <div className="dbi-metric-row dbi-metric-row-new">
                <div className="dbi-metric-lable">
                  {formatMessage({ id: 'dbi_top_metrictype' })}
                </div>
                <div className="dbi-metric-context">
                  <Segment
                    value={metricTypeValue}
                    onChange={onChangeSub(metricTypeValue, setMetricTypeValue)}
                    options={renderMetricsOptions({
                      opts: metricTypeOptions,
                      hasAll: true,
                      showTotal: false,
                      showIcon: false,
                    })}
                  />
                </div>
              </div>

              {/* Sources 数据源类型 Segment 选项 */}
              <div className="dbi-metric-row dbi-metric-row-new">
                <div className="dbi-metric-lable">{formatMessage({ id: 'dbi_top_sources' })}</div>
                <div className="dbi-metric-context">
                  <SegmentMultiple
                    value={sourcesValue}
                    onChange={createOnChange(sourcesValue, setSourcesValue) as any}
                    options={renderMetricsOptionsDom}
                  />
                </div>
              </div>

              {/* Metrics 当前指标展示 */}
              <div className="dbi-metric-row dbi-metric-row-new">
                <div className="dbi-metric-lable other-top">
                  {formatMessage({ id: 'dbi_label_metrics' })}
                </div>
                <div className="dbi-metric-context">
                  <div className="metric-box">
                    <MetricBox
                      onChange={onChangeSub(metricsValue, setMetricsValue)}
                      metricsValue={metricsValue}
                      options={metricOptionsFromBoth}
                      dateGranularityType={dateTypeValue || type}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dropdown>
    </>
  );
};

export default MetricSelect;
